import { useCallback } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { ReturnSettlement, ReturnVerification } from '../types/receipts.types';
import type { ReturnVerificationRecord } from '../../bottleReturns/types/bottleReturns.types';
import { creditsApi } from '../../credits/api/credits.api';
import { bottleReturnsApi } from '../../bottleReturns/api/bottleReturns.api';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface useReceiptHooksParams {
  addToast: (message: string, type?: ToastType) => void;
}

export const useReceiptHooks = ({ addToast }: useReceiptHooksParams) => {
  const queryClient = useQueryClient();

  const fetchReturnVerifications = useCallback(async () => {
    const data = await bottleReturnsApi.listVerifications();
    const list = Array.isArray(data.data) ? data.data : [];
    const mappedList = list.map((record: ReturnVerificationRecord): ReturnVerification => ({
      id: record.id,
      orderId: '',
      driverId: '',
      customerId: record.userId,
      recognizedCount: Number(record.count || 0),
      unrecognizedCount: 0,
      submittedAt: record.submittedAt,
      status: String(record.status || 'SUBMITTED').toUpperCase() as ReturnVerification['status'],
      reviewNotes: record.reviewNote
    }));
    return mappedList;
  }, []);

  const fetchReturnSettlements = useCallback(async () => {
    try {
      const data = await creditsApi.ledger({ page: 1, limit: 100 });
      /** Ledger RETURN-origin rows approximate settlements until a dedicated settlements API exists. */
      const list: ReturnSettlement[] = data.items
        .filter((e) => e.origin === 'RETURN')
        .map((entry) => ({
          id: entry.id,
          verificationId: entry.referenceId || '',
          finalAcceptedCount: 0,
          creditAmount: entry.delta,
          cashAmount: 0,
          feesApplied: 0,
          settledAt: entry.createdAt,
          settledBy: 'credit_ledger'
        }));
      return list;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not fetch settlements';
      addToast(message, 'warning');
      return [];
    }
  }, [addToast]);

  const settleReturnVerificationMutation = useMutation({
    mutationFn: async ({
      verificationId,
      finalAcceptedCount
    }: {
      verificationId: string;
      finalAcceptedCount: number;
    }) => {
      const safeAcceptedCount = Math.max(0, Math.floor(Number(finalAcceptedCount) || 0));
      const status = safeAcceptedCount > 0 ? 'approved' : 'rejected';
      await bottleReturnsApi.reviewVerification(verificationId, {
        status,
        reviewNote: status === 'approved'
          ? `Verified ${safeAcceptedCount} containers from owner console.`
          : 'Rejected from owner console: no containers verified.',
        ...(status === 'approved' ? { finalAcceptedCount: safeAcceptedCount } : {})
      });
    },
    onMutate: async ({ verificationId }) => {
      await queryClient.cancelQueries({ queryKey: ['returnVerifications'] });
      await queryClient.cancelQueries({ queryKey: ['returnSettlements'] });

      const previousVerifications = queryClient.getQueryData<ReturnVerification[]>(['returnVerifications']);
      const previousSettlements = queryClient.getQueryData<ReturnSettlement[]>(['returnSettlements']);

      queryClient.setQueryData<ReturnVerification[]>(['returnVerifications'], (old) => {
        if (!old) return [];
        return old.map((v) =>
          v.id === verificationId
            ? { ...v, status: 'APPROVED' }
            : v
        );
      });

      return { previousVerifications, previousSettlements };
    },
    onError: (err, variables, context) => {
      if (context) {
        queryClient.setQueryData(['returnVerifications'], context.previousVerifications);
        queryClient.setQueryData(['returnSettlements'], context.previousSettlements);
      }
      const message = err instanceof Error ? err.message : 'Could not settle return verification';
      addToast(message, 'error');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['returnVerifications'] });
      void queryClient.invalidateQueries({ queryKey: ['returnSettlements'] });
    },
    onSuccess: () => {
      addToast('Return verification settled successfully', 'success');
    }
  });

  /**
   * Credit/cash amounts are server-computed from the verified count; the extra parameters are
   * accepted only so the existing useNinpoCore call site keeps compiling until it is removed.
   */
  const settleReturnVerification = useCallback(
    async (verificationId: string, finalAcceptedCount: number, _creditAmount: number, _cashAmount: number) => {
      await settleReturnVerificationMutation.mutateAsync({ verificationId, finalAcceptedCount });
    },
    [settleReturnVerificationMutation]
  );

  return { fetchReturnVerifications, fetchReturnSettlements, settleReturnVerification };
};

